import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import apiClient from "@/api/client";

export type FeedbackTriageStatus = "new" | "triaged" | "in_progress" | "shipped" | "declined";

export const TRIAGE_STATUS_LABELS: Record<FeedbackTriageStatus, string> = {
  new: "New",
  triaged: "Triaged",
  in_progress: "In progress",
  shipped: "Shipped",
  declined: "Declined",
};

export const TRIAGE_STATUS_ORDER: FeedbackTriageStatus[] = [
  "new",
  "triaged",
  "in_progress",
  "shipped",
  "declined",
];

export interface FeedbackItem {
  id: string;
  source_article_id: string;
  title: string;
  content_markdown: string;
  category: "feature_request" | "bug_report";
  status: string;
  tags: string[] | null;
  source_created_at: string;
  source_updated_at: string;
  triage_status: FeedbackTriageStatus;
  triage_notes: string | null;
  // Layer 2: a 3-5 step implementation plan generated by Gemini when
  // autonomous triage decides an item is in_progress. Null otherwise.
  implementation_plan: string | null;
  triage_updated_at: string | null;
  // Evelina's reply, posted on prod and mirrored to staging by the
  // nightly mirror job so Claude reads it during the self-test loop.
  owner_response: string | null;
  owner_response_updated_at: string | null;
  first_mirrored_at: string;
  last_mirrored_at: string;
}

export interface FeedbackInboxResponse {
  items: FeedbackItem[];
  total: number;
}

export function useFeedbackInbox(filters?: {
  triage_status?: FeedbackTriageStatus;
  category?: string;
}) {
  const params = new URLSearchParams();
  if (filters?.triage_status) params.set("triage_status", filters.triage_status);
  if (filters?.category) params.set("category", filters.category);
  return useQuery<FeedbackInboxResponse>({
    queryKey: ["feedback-inbox", filters],
    queryFn: async () => {
      const res = await apiClient.get<FeedbackInboxResponse>(
        `/feedback/inbox?${params.toString()}`,
      );
      return res.data;
    },
    refetchInterval: 60_000, // freshen every minute so triage changes from
    // other tabs / the mirror job land without manual refresh
  });
}

export function useUpdateTriage() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      id,
      triage_status,
      triage_notes,
    }: {
      id: string;
      triage_status?: FeedbackTriageStatus;
      triage_notes?: string;
    }) => {
      const body: Record<string, unknown> = {};
      if (triage_status) body.triage_status = triage_status;
      if (triage_notes !== undefined) body.triage_notes = triage_notes;
      const res = await apiClient.patch<FeedbackItem>(`/feedback/inbox/${id}`, body);
      return res.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["feedback-inbox"] }),
  });
}

export function useUpdateOwnerResponse() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, owner_response }: { id: string; owner_response: string }) => {
      const res = await apiClient.patch<FeedbackItem>(`/feedback/inbox/${id}/owner-response`, {
        owner_response,
      });
      return res.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["feedback-inbox"] }),
  });
}

export interface FeedbackMirrorRun {
  id: string;
  started_at: string;
  completed_at: string | null;
  rows_seen: number | null;
  rows_inserted: number | null;
  rows_updated: number | null;
  error_message: string | null;
}

export function useFeedbackMirrorRuns() {
  return useQuery<FeedbackMirrorRun[]>({
    queryKey: ["feedback-mirror-runs"],
    queryFn: async () => {
      const res = await apiClient.get<FeedbackMirrorRun[]>("/feedback/mirror-runs");
      return res.data;
    },
  });
}
